import SvgIcon from '@material-ui/core/SvgIcon'
import { withStyles } from '@material-ui/core/styles'
import Tooltip from '@material-ui/core/Tooltip'
import classNames from 'classnames/bind'
import { isObject } from 'lodash'
import React from 'react'

import IconData from './IconData.json'

const arrowGenerator = color => ({
  '&[x-placement*="bottom"] $arrow': {
    top: 0,
    left: 0,
    marginTop: '-0.95em',
    width: '3em',
    height: '1em',
    '&::before': {
      borderWidth: '0 1em 1em 1em',
      borderColor: `transparent transparent ${color} transparent`,
    },
  },
  '&[x-placement*="top"] $arrow': {
    bottom: 0,
    left: 0,
    marginBottom: '-0.95em',
    width: '3em',
    height: '1em',
    '&::before': {
      borderWidth: '1em 1em 0 1em',
      borderColor: `${color} transparent transparent transparent`,
    },
  },
  '&[x-placement*="right"] $arrow': {
    left: 0,
    marginLeft: '-0.95em',
    height: '3em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 1em 1em 0',
      borderColor: `transparent ${color} transparent transparent`,
    },
  },
  '&[x-placement*="left"] $arrow': {
    right: 0,
    marginRight: '-0.95em',
    height: '3em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 0 1em 1em',
      borderColor: `transparent transparent transparent ${color}`,
    },
  },
})

const styles = theme => ({
  svgIcon: {
    '&.colorSuccess': {
      color: theme.status.successIcon,
      fill: theme.status.successIcon,
      stroke: theme.status.successIcon,
    },
    '&.colorError': {
      color: theme.status.errorIcon,
      fill: theme.status.errorIcon,
      stroke: theme.status.errorIcon,
    },
    '&.colorDisabled': {
      color: theme.status.disabledIcon,
      fill: theme.status.disabledIcon,
      stroke: theme.status.disabledIcon,
    },
    '&.colorDefault': {
      color: 'inherit',
      fill: props => (props.withBorder ? `var(--gameday-body-textColorAlt, ${theme.body.textColorAlt})` : null),
      stroke: props => (props.withBorder ? `var(--gameday-body-textColorAlt, ${theme.body.textColorAlt})` : null),
    },
    '&.colorWarning': {
      color: theme.status.warningIcon,
      fill: theme.status.warningIcon,
      stroke: theme.status.warningIcon,
    },
    '&.colorInfo': {
      color: theme.status.infoIcon,
      fill: theme.status.infoIcon,
      stroke: theme.status.infoIcon,
    },
    '&.colorDark': {
      color: theme.colors.navy,
      fill: theme.colors.navy,
      stroke: theme.colors.navy,
    },
    '&.colorBlack': {
      color: theme.colors.black,
      fill: theme.colors.black,
      stroke: theme.colors.black,
    },
    '&.colorWhite': {
      color: theme.colors.white,
      fill: theme.colors.white,
      stroke: theme.colors.white,
    },
    '&.colorGrey': {
      color: theme.colors.midGrey,
      fill: theme.colors.midGrey,
      stroke: theme.colors.midGrey,
    },
    '&.disabledIcon': {
      opacity: 0.7,
      pointerEvents: 'none',
    },
  },
  arrow: {
    position: 'absolute',
    fontSize: 6,
    width: '3em',
    height: '3em',
    '&::before': {
      content: '""',
      margin: 'auto',
      display: 'block',
      width: 0,
      height: 0,
      borderStyle: 'solid',
    },
  },
  bootstrapPopper: arrowGenerator(theme.colors.black),
  bootstrapTooltip: {
    backgroundColor: theme.colors.black,
  },
  bootstrapPlacementLeft: {
    margin: '0 8px',
  },
  bootstrapPlacementRight: {
    margin: '0 8px',
  },
  bootstrapPlacementTop: {
    margin: '8px 0',
  },
  bootstrapPlacementBottom: {
    margin: '8px 0',
  },
  iconWrapper: {
    width: props => props.iconWidth || 'auto',
    minWidth: props => props.iconWidth || '1rem',
    overflow: 'hidden',
    '&.disabledCursor': {
      cursor: 'not-allowed !important',
    },
  },
})
class Icon extends React.Component {
  state = {
    arrowRef: null,
  }

  handleArrowRef = node => {
    this.setState({
      arrowRef: node,
    })
  }

  render() {
    const {
      iconSize,
      iconName,
      iconType,
      iconWidth,
      classes,
      className,
      color,
      viewBox,
      toolTipTitle,
      withBorder,
      disabled,
      ...rest
    } = this.props

    let colorClass = ''
    if (iconType === 'success') {
      colorClass = 'colorSuccess'
    } else if (iconType === 'error') {
      colorClass = 'colorError'
    } else if (iconType === 'disabled') {
      colorClass = 'colorDisabled'
    } else if (iconType === 'warning') {
      colorClass = 'colorWarning'
    } else if (iconType === 'info') {
      colorClass = 'colorInfo'
    } else if (iconType === 'dark') {
      colorClass = 'colorDark'
    } else if (iconType === 'black') {
      colorClass = 'colorBlack'
    } else if (iconType === 'white') {
      colorClass = 'colorWhite'
    } else if (iconType === 'grey') {
      colorClass = 'colorGrey'
    } else {
      colorClass = 'colorDefault'
    }
    return (
      <>
        {toolTipTitle ? (
          <Tooltip
            title={
              <>
                {toolTipTitle}
                <span className={classes.arrow} ref={this.handleArrowRef} />
              </>
            }
            classes={{
              tooltip: classes.bootstrapTooltip,
              popper: classes.bootstrapPopper,
              tooltipPlacementLeft: classes.bootstrapPlacementLeft,
              tooltipPlacementRight: classes.bootstrapPlacementRight,
              tooltipPlacementTop: classes.bootstrapPlacementTop,
              tooltipPlacementBottom: classes.bootstrapPlacementBottom,
            }}
            PopperProps={{
              popperOptions: {
                modifiers: {
                  arrow: {
                    enabled: Boolean(this.state.arrowRef),
                    element: this.state.arrowRef,
                  },
                },
              },
            }}
            placement="top"
          >
            <span className={classNames(classes.iconWrapper, 'custom-icon', disabled ? 'disabledCursor' : '')}>
              <SvgIcon
                className={classNames(classes.svgIcon, colorClass, className, disabled ? 'disabledIcon' : '')}
                style={{
                  fontSize: `${iconSize}px`,
                  color: `${color}`,
                }}
                viewBox={viewBox}
                {...rest}
              >
                {withBorder ? <IconBorder /> : null}
                {renderIconPaths(iconName)}
              </SvgIcon>
            </span>
          </Tooltip>
        ) : (
          <span className={classNames(classes.iconWrapper, 'custom-icon', disabled ? 'disabledCursor' : '')}>
            <SvgIcon
              className={classNames(classes.svgIcon, colorClass, className, disabled ? 'disabledIcon' : '')}
              style={{
                fontSize: `${iconSize}px`,
                color: `${color}`,
              }}
              viewBox={viewBox}
              {...rest}
            >
              {withBorder ? <IconBorder /> : null}
              {renderIconPaths(iconName)}
            </SvgIcon>
          </span>
        )}
      </>
    )
  }
}

const renderIconPaths = iconName => {
  const iconData = IconData[iconName]
  if (Array.isArray(iconData)) {
    return (
      <>
        {iconData.map((path, i) => {
          let props = {}
          let pathVal = path
          if (isObject(path)) {
            props = {
              fillRule: path.fillRule,
              clipRule: path.clipRule,
            }
            pathVal = path.d
          }
          // biome-ignore lint/suspicious/noArrayIndexKey: TODO
          return <path key={i} d={pathVal} stroke="none" {...props} />
        })}
      </>
    )
  }
  return <path d={iconData} stroke="none" />
}

const IconBorder = () => <rect x="5.375" y="5.375" width="15.25" height="15.25" rx="1.5" fill="none" />

Icon.defaultProps = {
  iconSize: 16,
  viewBox: '0 0 24 24',
  toolTipTitle: '',
}

const IconComponent = withStyles(styles)(Icon)
IconComponent.displayName = 'Icon'
export default IconComponent
